import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSend, FiGrid, FiZap } from 'react-icons/fi';
import SafeAnimatePresence from '../../components/ui/SafeAnimatePresence';
import AppLoadingSkeleton from '../../components/ui/AppLoadingSkeleton';
import { useAmps } from '../../context/AmpContext';
import { useChannels } from '../../context/ChannelContext';
import iaaiLogo from '../../assets/images/M_Multicolor.svg';
import { ROUTES } from '../../app/routes';
import './StartScreen.css';

const FIXED_DESTINATIONS = [
  { id: 'marketplace',    name: 'Marketplace',    path: '/marketplace',    color: '#7c3aed' },
  { id: 'momentum-claw',  name: 'MomentumClaw',  path: '/momentum-claw/', color: '#0891b2' },
];

const StartScreen: React.FC = () => {
  const navigate = useNavigate();
  const { amps, premadeAmps, loading: ampsLoading } = useAmps();
  const { channels, loading: channelsLoading } = useChannels();
  const [value, setValue] = useState('');
  const [launching, setLaunching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Rate SSO hand-off: skip chat/welcome entirely, land on the rating tool
  // the source tool asked for (isPL wins if both isPL and isCL are set).
  useEffect(() => {
    if (localStorage.getItem('sourceTool') !== 'Rate') return;
    const isCLOnly = localStorage.getItem('isCL') === 'true' && localStorage.getItem('isPL') !== 'true';
    navigate(isCLOnly ? ROUTES.COMMERCIAL_LINES_RATING : ROUTES.RATE_QUOTING, { replace: true });
  }, [navigate]);

  // Auto-redirect to welcome channel (or /chat) on mount.
  // Skip while a post-login redirect is in flight (loginPending flag set by LoginPage).
  useEffect(() => {
    if (localStorage.getItem('sourceTool') === 'Rate') return;
    if (channelsLoading) return;
    const showWelcome = localStorage.getItem('showWelcomeChannel') !== 'false';
    if (!showWelcome || channels.length > 1) {
      navigate('/chat', { replace: true });
      return;
    }
    const welcome = channels.find(c => c.name?.toLowerCase() === 'welcome');
    const first = channels[0];
    if (welcome) navigate(`/channel/${welcome.guid}`, { replace: true });
    else if (first) navigate(`/channel/${first.guid}`, { replace: true });
    else navigate('/chat', { replace: true });
  }, [channelsLoading, channels, navigate]);

  // Derive list visibility and search term from value
  const showList = value.trimStart().startsWith('/');
  const rawSearch = showList ? value.trimStart().slice(1).toLowerCase().trim() : '';
  // '/start' is a special alias for "show all"
  const searchTerm = rawSearch === 'start' ? '' : rawSearch;

  const allAmps = useMemo(
    () => [...premadeAmps, ...amps.filter(a => !premadeAmps.find(p => p.id === a.id))],
    [premadeAmps, amps]
  );

  // Filter channels by search term
  const filteredChannels = useMemo(() => {
    if (!searchTerm) return channels;
    return channels.filter(ch => ch.name.toLowerCase().includes(searchTerm));
  }, [channels, searchTerm]);

  // Filter AMPs by name or category
  const filteredAmps = useMemo(() => {
    if (!searchTerm) return allAmps;
    return allAmps.filter(
      a =>
        a.name.toLowerCase().includes(searchTerm) ||
        a.category?.toLowerCase().includes(searchTerm)
    );
  }, [allAmps, searchTerm]);

  // Group filtered AMPs by category
  const ampsByCategory = useMemo(() => {
    const map = new Map<string, typeof allAmps>();
    for (const amp of filteredAmps) {
      const cat = amp.category?.trim() || 'General';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(amp);
    }
    return Array.from(map.entries());
  }, [filteredAmps]);

  // Filter fixed destinations by search term
  const filteredDestinations = useMemo(() => {
    if (!searchTerm) return FIXED_DESTINATIONS;
    return FIXED_DESTINATIONS.filter(d => d.name.toLowerCase().includes(searchTerm));
  }, [searchTerm]);

  const hasResults =
    filteredDestinations.length > 0 || filteredChannels.length > 0 || filteredAmps.length > 0;

  const launchTo = useCallback((path: string) => {
    setLaunching(true);
    setTimeout(() => navigate(path), 800);
  }, [navigate]);

  const goToWelcome = useCallback(() => {
    const showWelcome = localStorage.getItem('showWelcomeChannel') !== 'false';
    const welcome = showWelcome ? channels.find(c => c.name?.toLowerCase() === 'welcome') : null;
    const first = channels[0];
    if (welcome) launchTo(`/channel/${welcome.guid}`);
    else if (first) launchTo(`/channel/${first.guid}`);
    else launchTo('/chat');
  }, [channels, launchTo]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      if (value.trimStart().toLowerCase() === '/start') {
        goToWelcome();
      } else if (value.trimStart().startsWith('/')) {
        // Clear filter → show all
        setValue('/');
      }
    }
  }, [value, goToWelcome]);

  const handleSendClick = useCallback(() => {
    if (value.trimStart().toLowerCase() === '/start') {
      goToWelcome();
    } else if (value.trimStart().startsWith('/')) {
      setValue('/');
    }
  }, [value, goToWelcome]);

  // Subtitle text
  const subtitle = useMemo(() => {
    if (!showList) return 'Type / to explore your workspace';
    if (searchTerm) return `Filtering by "${searchTerm}"`;
    return 'Select a Workspace or AMP to get started';
  }, [showList, searchTerm]);

  if (channelsLoading) return null;

  return (
    <div className="start-screen">
      <div className="start-screen__bg-glow" />

      <div className="start-screen__content">
        {/* Branding */}
        <motion.div
          className="start-screen__brand"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="start-screen__logo">
            <img src={iaaiLogo} alt="Insurance Agency AI" />
          </div>
          <h1 className="start-screen__title">Insurance Agency AI</h1>
          <p className="start-screen__subtitle">{subtitle}</p>
        </motion.div>

        {/* Chat Input */}
        <motion.div
          className="start-screen__input-wrap"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <div className="start-screen__field-wrap">
            <input
              ref={inputRef}
              className="start-screen__field"
              placeholder="Type / to search Skills and Channels…"
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
            />
            <motion.button
              className="start-screen__send-btn"
              type="button"
              disabled={!value.trim()}
              onClick={handleSendClick}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.92 }}
            >
              <FiSend size={16} />
            </motion.button>
          </div>
        </motion.div>

        {/* Channel + AMP list — below input */}
        <SafeAnimatePresence>
          {showList && (
            <motion.div
              className="start-screen__list"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.25 }}
            >
              {/* No results */}
              {!ampsLoading && !channelsLoading && !hasResults && (
                <div className="start-screen__no-results">
                  No results for "{searchTerm}"
                </div>
              )}

              {filteredDestinations.length > 0 && (
                <div className="start-screen__section">
                  <div className="start-screen__section-header">
                    <FiGrid size={12} />
                    <span>Apps</span>
                  </div>
                  <div className="start-screen__pills">
                    {filteredDestinations.map((dest, i) => (
                      <motion.button
                        key={dest.id}
                        className="start-screen__pill glass-tag"
                        onClick={() => launchTo(dest.path)}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.12, delay: i * 0.025 }}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                      >
                        <span className="start-screen__pill-dot" style={{ background: dest.color }} />
                        {dest.name}
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Divider: Apps → Channels ── */}
              {filteredDestinations.length > 0 && !channelsLoading && filteredChannels.length > 0 && (
                <div className="start-screen__divider" />
              )}

              {/* ── Channels ── */}
              {!channelsLoading && filteredChannels.length > 0 && (
                <div className="start-screen__section">
                  <div className="start-screen__section-header">
                    <FiGrid size={12} />
                    <span>Workspaces</span>
                  </div>
                  <div className="start-screen__pills">
                    {filteredChannels.map((ch, i) => (
                      <motion.button
                        key={ch.guid}
                        className="start-screen__pill glass-tag"
                        onClick={() => launchTo(`/channel/${ch.guid}`)}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.12, delay: i * 0.025 }}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                      >
                        <span className="start-screen__pill-dot" style={{ background: ch.color || '#3f5782' }} />
                        {ch.name}
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Divider between Channels and AMPs ── */}
              {!channelsLoading && filteredChannels.length > 0 && !ampsLoading && filteredAmps.length > 0 && (
                <div className="start-screen__divider" />
              )}

              {/* ── AMPs grouped by category ── */}
              {!ampsLoading && ampsByCategory.length > 0 && (
                <div className="start-screen__section">
                  <div className="start-screen__section-header">
                    <FiZap size={12} />
                    <span>Skills</span>
                  </div>

                  {ampsByCategory.map(([category, catAmps], catIndex) => (
                    <div key={category} className="start-screen__category">
                      {/* Sub-separator before each category except the first */}
                      {catIndex > 0 && <div className="start-screen__sub-divider" />}

                      <div className="start-screen__category-label">{category}</div>

                      <div className="start-screen__pills">
                        {catAmps.map((amp, i) => (
                          <motion.button
                            key={amp.id}
                            className="start-screen__pill glass-tag"
                            onClick={() => {
                              const cat = amp.category;
                              if (cat === 'Momentum Voice' || cat === 'Voice') launchTo(ROUTES.VOICE);
                              else if (cat === 'Quote Proposal') launchTo(ROUTES.QUOTE_PROPOSAL);
                              else if (cat === 'Carrier Download') launchTo(ROUTES.CARRIER_DOWNLOAD);
                              else launchTo(`/amp/${amp.id}`);
                            }}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.12, delay: (catIndex * 0.05) + i * 0.025 }}
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.96 }}
                          >
                            <span className="start-screen__pill-dot" style={{ background: amp.color || '#3f5782' }} />
                            {amp.name}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(ampsLoading || channelsLoading) && (
                <div className="start-screen__loading">Loading…</div>
              )}
            </motion.div>
          )}
        </SafeAnimatePresence>
      </div>

      <AppLoadingSkeleton visible={launching} />
    </div>
  );
};

export default StartScreen;
